import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useOfferedProducts } from 'src/graphql';
import { useStripeSubscriptionSchedule } from 'src/graphql/entities/stripe-subscription-schedule';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { TRIAL_MEMBERSHIP_LENGTH_DAYS } from './const';
import { getTrialProducts } from './util';

interface ActivateTrialMembershipFormValues {
  partnerProductId: string;
}

/**
 * One-time trial activation at Basic Support (1V-5895). Deliberately separate
 * from AddComplimentaryPeriodModal: the plan list is capped to the pilot's two
 * memberships, the length is fixed, and submitting also stamps the address's
 * "Trial Membership Activated At" custom attribute, which is what hides the
 * action afterwards and what the Trial Eligible support policy conditions on.
 */
export const ActivateTrialMembershipModal = () => {
  const { closeModal } = useModalMethods(Modals.ActivateTrialMembership);
  const { isOpened, modalProps } = useModalState(Modals.ActivateTrialMembership);
  const { activateTrialMembership } = useStripeSubscriptionSchedule();

  const address = modalProps?.address;
  const { offeredProducts, loading: isLoadingOfferedProducts } = useOfferedProducts({
    brandId: address?.brandId,
    partnerId: address?.partnerId,
  });

  const trialProducts = useMemo(() => getTrialProducts(offeredProducts), [offeredProducts]);

  const expirationDate = useMemo(
    () => dayjs().add(TRIAL_MEMBERSHIP_LENGTH_DAYS, 'day'),
    // recompute per open so a long-lived tab still stamps "today + 60"
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpened],
  );

  const formMethods = useForm<ActivateTrialMembershipFormValues>({
    defaultValues: { partnerProductId: '' },
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    if (trialProducts.length) {
      setValue('partnerProductId', trialProducts[0].partnerProductId);
    }
  }, [trialProducts, setValue]);

  const handleClose = useCallback(() => {
    formMethods.reset();
    closeModal();
  }, [closeModal, formMethods]);

  const onSubmit = useCallback(
    async (data: ActivateTrialMembershipFormValues) => {
      if (!address?.addressId || !address?.partnerId) {
        return;
      }

      const { addressId } = address;
      formMethods.reset();
      closeModal();

      // the backend enforces eligibility and writes the one-per-site stamp
      await activateTrialMembership({
        addressId,
        partnerId: address.partnerId,
        cpp: {
          partnerProductId: data.partnerProductId,
          expDate: expirationDate.format('YYYY-MM-DD'),
        },
      });
    },
    [activateTrialMembership, address, closeModal, expirationDate, formMethods],
  );

  return (
    <Dialog open={isOpened} onClose={handleClose} fullWidth>
      <DialogTitle>
        <Typography>Activate Trial Membership</Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="trial-membership-label">Membership</InputLabel>
            <Controller
              name="partnerProductId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="trial-membership-label"
                  label="Membership"
                  size="small"
                  disabled={isLoadingOfferedProducts}
                  value={field.value || ''}
                >
                  {trialProducts.map((product) => (
                    <MenuItem key={product.partnerProductId} value={product.partnerProductId}>
                      {formatMembershipName(product.displayName)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {!isLoadingOfferedProducts && !trialProducts.length ? (
              <FormHelperText error>
                This partner offers none of the pilot memberships
              </FormHelperText>
            ) : null}
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Active Until"
              value={expirationDate.format('MMM D, YYYY')}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <FormHelperText>
              One {TRIAL_MEMBERSHIP_LENGTH_DAYS}-day trial per site, funded by the partner.
            </FormHelperText>
          </FormControl>

          <DialogActions sx={{ marginTop: '1rem' }}>
            <Button
              disabled={isSubmitting || !trialProducts.length}
              variant="text"
              color="primary"
              type="submit"
            >
              Activate Trial
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
